import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { IndividualDetail } from "./individuals.entity";
import {
  getIndividualDetail,
  updateIndividualServices,
  uploadIndividualDocument,
  deleteIndividualDocument,
  listIndividualDocuments,
  updateIndividualDocumentExpiresAt,
} from "./individuals.api";
import { uuid } from "@/interfaces";
import type { DocumentRecord } from "@shyne/shared/api";
import { ReadAllResponse } from "@/interfaces";

interface IndividualDetailState {
  detail: IndividualDetail | null;
  loading: boolean;
  error: string | null;

  // Document management
  documents: DocumentRecord[];
  documentsLoading: boolean;
  documentsTotal: number;
  documentsPage: number;
  documentsPageSize: number;
  uploadModalOpen: boolean;
  uploadingDocument: boolean;
  uploadError: string | null;
  updatingDocumentId: uuid | null;

  // Service management
  savingServices: string[]; // Track which services are currently being saved
  serviceError: string | null;
}

const initialState: IndividualDetailState = {
  detail: null,
  loading: false,
  error: null,
  documents: [],
  documentsLoading: false,
  documentsTotal: 0,
  documentsPage: 1,
  documentsPageSize: 10,
  uploadModalOpen: false,
  uploadingDocument: false,
  uploadError: null,
  updatingDocumentId: null,
  savingServices: [],
  serviceError: null,
};

export const fetchIndividualDetail = createAsyncThunk("individualDetail/fetch", async (orgIndividualId: uuid) => {
  return await getIndividualDetail(orgIndividualId);
});

export const updateServices = createAsyncThunk(
  "individualDetail/updateServices",
  async ({ orgIndividualId, services }: { orgIndividualId: uuid; services: uuid[] }) => {
    await updateIndividualServices(orgIndividualId, services);
    return services;
  }
);

export const toggleService = createAsyncThunk(
  "individualDetail/toggleService",
  async (
    {
      orgIndividualId,
      serviceId,
      currentServices,
    }: {
      orgIndividualId: uuid;
      serviceId: string;
      currentServices: string[];
    },
    { rejectWithValue }
  ) => {
    try {
      const isCurrentlySelected = currentServices.includes(serviceId);
      const newServices = isCurrentlySelected
        ? currentServices.filter((id) => id !== serviceId)
        : [...currentServices, serviceId];

      await updateIndividualServices(orgIndividualId, newServices);

      return { serviceId, newServices };
    } catch (error: any) {
      return rejectWithValue({ serviceId, error: error.message });
    }
  }
);

export const uploadDocument = createAsyncThunk(
  "individualDetail/uploadDocument",
  async ({ orgIndividualId, file, expiresAt }: { orgIndividualId: uuid; file: File; expiresAt?: string | Date }) => {
    return await uploadIndividualDocument(orgIndividualId, file, expiresAt);
  }
);

export const removeDocument = createAsyncThunk("individualDetail/removeDocument", async (docId: string) => {
  await deleteIndividualDocument(docId);
  return docId;
});

export const fetchDocuments = createAsyncThunk(
  "individualDetail/fetchDocuments",
  async ({ orgIndividualId, page, pageSize }: { orgIndividualId: uuid; page: number; pageSize: number }) => {
    return await listIndividualDocuments(orgIndividualId, page, pageSize);
  }
);

export const updateDocumentExpiresAt = createAsyncThunk(
  "individualDetail/updateDocumentExpiresAt",
  async ({ docId, expiresAt }: { docId: string; expiresAt: string | Date | null }) => {
    return await updateIndividualDocumentExpiresAt(docId, expiresAt);
  }
);

const updateDocumentInList = (documents: DocumentRecord[], updatedDocument: DocumentRecord) =>
  documents.map((document) => (document.id === updatedDocument.id ? updatedDocument : document));

const individualDetailSlice = createSlice({
  name: "individualDetail",
  initialState,
  reducers: {
    setUploadModalOpen(state, action: PayloadAction<boolean>) {
      state.uploadModalOpen = action.payload;
      if (!action.payload) {
        state.uploadError = null;
      }
    },
    setDocumentsPage(state, action: PayloadAction<number>) {
      state.documentsPage = action.payload;
    },
    setDocumentsPageSize(state, action: PayloadAction<number>) {
      state.documentsPageSize = action.payload;
    },
    clearDetail(state) {
      return initialState;
    },
    optimisticallyToggleService(state, action: PayloadAction<{ serviceId: string; isAdding: boolean }>) {
      if (state.detail) {
        const { serviceId, isAdding } = action.payload;
        const currentServices = state.detail.serviceIds;
        state.detail.serviceIds = isAdding ? [...currentServices, serviceId] : currentServices.filter((id) => id !== serviceId);
      }
    },
    revertServiceToggle(state, action: PayloadAction<{ serviceId: string; previousServices: string[] }>) {
      if (state.detail) {
        state.detail.serviceIds = action.payload.previousServices;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch detail
      .addCase(fetchIndividualDetail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchIndividualDetail.fulfilled, (state, action) => {
        state.loading = false;
        state.detail = action.payload;
        state.documents = action.payload.documents;
        state.documentsTotal = action.payload.documents.length;
      })
      .addCase(fetchIndividualDetail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? "Failed to load individual details";
      })

      // Update services (batch)
      .addCase(updateServices.pending, (state) => {
        state.loading = true;
        state.serviceError = null;
      })
      .addCase(updateServices.fulfilled, (state, action) => {
        state.loading = false;
        if (state.detail) {
          state.detail.serviceIds = action.payload;
        }
      })
      .addCase(updateServices.rejected, (state, action) => {
        state.loading = false;
        state.serviceError = action.error.message ?? "Failed to update services";
      })

      // Toggle individual service
      .addCase(toggleService.pending, (state, action) => {
        const serviceId = action.meta.arg.serviceId;
        if (!state.savingServices.includes(serviceId)) {
          state.savingServices.push(serviceId);
        }
        state.serviceError = null;
      })
      .addCase(toggleService.fulfilled, (state, action) => {
        const { serviceId } = action.payload;
        state.savingServices = state.savingServices.filter((id) => id !== serviceId);
      })
      .addCase(toggleService.rejected, (state, action) => {
        const serviceId = (action.payload as any)?.serviceId || action.meta.arg.serviceId;
        state.savingServices = state.savingServices.filter((id) => id !== serviceId);
        state.serviceError = (action.payload as any)?.error || action.error.message;
      })

      // Upload document
      .addCase(uploadDocument.pending, (state) => {
        state.uploadingDocument = true;
        state.uploadError = null;
      })
      .addCase(uploadDocument.fulfilled, (state, action) => {
        state.uploadingDocument = false;
        state.uploadModalOpen = false;
        state.documents.unshift(action.payload);
        state.documentsTotal += 1;
        if (state.detail) {
          state.detail.documents.unshift(action.payload);
        }
      })
      .addCase(uploadDocument.rejected, (state, action) => {
        state.uploadingDocument = false;
        state.uploadError = action.error.message ?? "Failed to upload document";
      })

      // Remove document
      .addCase(removeDocument.fulfilled, (state, action) => {
        const docId = action.payload;
        state.documents = state.documents.filter((d) => d.id !== docId);
        state.documentsTotal -= 1;
        if (state.detail) {
          state.detail.documents = state.detail.documents.filter((d) => d.id !== docId);
        }
      })

      // Fetch documents
      .addCase(fetchDocuments.pending, (state) => {
        state.documentsLoading = true;
      })
      .addCase(fetchDocuments.fulfilled, (state, action) => {
        state.documentsLoading = false;
        state.documents = action.payload.data;
        state.documentsTotal = action.payload.count;
      })
      .addCase(fetchDocuments.rejected, (state) => {
        state.documentsLoading = false;
      })

      // Update document expiration
      .addCase(updateDocumentExpiresAt.pending, (state, action) => {
        state.updatingDocumentId = action.meta.arg.docId;
      })
      .addCase(updateDocumentExpiresAt.fulfilled, (state, action) => {
        state.updatingDocumentId = null;
        state.documents = updateDocumentInList(state.documents, action.payload);
        if (state.detail) {
          state.detail.documents = updateDocumentInList(state.detail.documents, action.payload);
        }
      })
      .addCase(updateDocumentExpiresAt.rejected, (state) => {
        state.updatingDocumentId = null;
      });
  },
});

export const {
  setUploadModalOpen,
  setDocumentsPage,
  setDocumentsPageSize,
  clearDetail,
  optimisticallyToggleService,
  revertServiceToggle,
} = individualDetailSlice.actions;

export default individualDetailSlice.reducer;
